import { sendGTMEvent } from '@next/third-parties/google'
import type { Stripe } from '@stripe/stripe-js'
import { useEffect, useState } from 'react'

import { stripeService } from '@/lib/stripe'

import styles from './Donations.module.css'

export interface Donation {
  amount: string
  dataID: string
  prodProductID?: string
  testProductID?: string
}

interface DonationsProps {
  donations: Donation[]
  title?: string
  description?: React.ReactNode
  terms?: string
  asset?: React.ReactNode
  variant?: 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'quinary'
}

export const Donations: React.FC<DonationsProps> = ({
  donations,
  title,
  description,
  terms,
  asset,
  variant = 'primary',
}: DonationsProps) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [stripe, setStripe] = useState<Stripe | null>(null)

  useEffect(() => {
    let mounted = true

    const initializeStripe = async () => {
      const stripeInstance = await stripeService.getStripe()

      if (mounted) {
        if (stripeInstance) {
          setStripe(stripeInstance)
        } else {
          console.error(
            '%c❌ Stripe Failed',
            'color: #ff4444; font-weight: bold;',
            'Failed to initialize Stripe'
          )
        }
      }
    }

    if (typeof window !== 'undefined') {
      initializeStripe()
    }

    return () => {
      mounted = false
      // Clean up Stripe when component unmounts
      stripeService.cleanup()
      setStripe(null)
    }
  }, [])

  const handleDonationClick = async (
    prodId: string | undefined,
    testId: string | undefined,
    amount: string
  ) => {
    if (isProcessing || !stripe) return

    try {
      setIsProcessing(true)

      // Send GTM event
      sendGTMEvent({ event: 'donate', value: amount })
      // Determine which Stripe ID to use
      const stripeID = process.env.NEXT_PUBLIC_SB_VERSION === 'published' ? prodId : testId

      if (!stripeID) {
        throw new Error('No valid Stripe product ID found')
      }

      // Get current URL for redirect
      const currentUrl = window.location.href.split('?')[0]

      // Redirect to checkout
      const result = await stripe.redirectToCheckout({
        lineItems: [{ price: stripeID, quantity: 1 }],
        mode: 'payment',
        successUrl: `${currentUrl}?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: currentUrl,
      })

      if (result.error) {
        throw new Error(result.error.message)
      }
    } catch (error) {
      console.error('%c❌ Donation Failed', 'color: #ff4444; font-weight: bold;', error)
      // Silent fail - errors will be handled by Stripe's UI
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className={styles[`variant-${variant}`]}>
      {title && <div className={styles.title}>{title}</div>}
      <div className={styles.holder}>
        {asset && (
          <div className={styles.asset} data-testid="asset-holder">
            {asset}
          </div>
        )}
        {donations?.map((item, index) => {
          return (
            <button
              key={index}
              onClick={() =>
                handleDonationClick(item.prodProductID, item.testProductID, item.amount)
              }
              className={styles.button}
              disabled={isProcessing || !stripe}
            >
              ${item.amount}
            </button>
          )
        })}
      </div>
      {description && <div className={styles.description}>{description}</div>}
      {terms && <div className={styles.terms}>{terms}</div>}
    </div>
  )
}
